import { useState } from "react";
import axios from "axios";
import { Send, Heart, Lock } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrayerRequest() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", isPrivate: false });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete all required fields", description: "We need your name, email, and prayer." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/prayers`, form);
      toast({ title: "Prayer received", description: "Our team will lift this up before the Lord. Be encouraged." });
      setForm({ name: "", email: "", subject: "", message: "", isPrivate: false });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Please try again in a moment.";
      toast({ title: "Couldn't send right now", description: typeof msg === "string" ? msg : "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="prayer" className="py-24 md:py-28 bg-[#f5f7f9]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="text-[#c8a36a] uppercase tracking-[0.32em] text-xs font-bold mb-3">Prayer Requests</div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a2d38] leading-[1.05] tracking-tight">
            We’d be honored to <span className="text-[#c8a36a]">pray with you.</span>
          </h2>
          <p className="mt-6 text-[#4a5b62] leading-relaxed text-[17px]">
            Whatever you’re carrying — share it. Our intercessors meet daily, and our Telegram prayer chain stands ready around the clock.
          </p>
          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-white border border-[#e8ecef] flex items-center justify-center flex-shrink-0">
                <Heart className="h-4 w-4 text-[#c8a36a]" />
              </div>
              <div>
                <div className="font-bold text-[#0a2d38]">Confidential & loved</div>
                <div className="text-sm text-[#7a8a8f]">Your request is held in confidence by our pastoral team.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-white border border-[#e8ecef] flex items-center justify-center flex-shrink-0">
                <Lock className="h-4 w-4 text-[#0a2d38]" />
              </div>
              <div>
                <div className="font-bold text-[#0a2d38]">Join our prayer chain</div>
                <a href="https://t.me/lwprayers" target="_blank" rel="noreferrer" className="text-sm text-[#0a2d38] underline underline-offset-4 hover:text-[#c8a36a]">t.me/lwprayers</a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-white rounded-md border border-[#e8ecef] p-7 md:p-9 shadow-[0_30px_60px_-40px_rgba(14,84,105,0.35)]">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-[0.22em] text-[#7a8a8f]">Name *</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 bg-white border-[#e8ecef]" placeholder="Your full name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.22em] text-[#7a8a8f]">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 bg-white border-[#e8ecef]" placeholder="you@example.com" />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="subject" className="text-xs uppercase tracking-[0.22em] text-[#7a8a8f]">Subject of Prayer</Label>
            <Input id="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="mt-2 bg-white border-[#e8ecef]" placeholder="e.g. Healing, family, guidance" />
          </div>
          <div className="mt-4">
            <Label htmlFor="message" className="text-xs uppercase tracking-[0.22em] text-[#7a8a8f]">Your Prayer *</Label>
            <Textarea id="message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-2 bg-white border-[#e8ecef]" placeholder="Share what is on your heart..." />
          </div>
          <div className="flex items-center gap-2 mt-5">
            <Checkbox id="private" checked={form.isPrivate} onCheckedChange={(v) => setForm({ ...form, isPrivate: !!v })} />
            <Label htmlFor="private" className="text-sm text-[#7a8a8f] cursor-pointer">Keep this request private (pastoral team only)</Label>
          </div>
          <button type="submit" disabled={submitting} className="mt-7 btn-teal rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {submitting ? "Sending..." : "Send Prayer Request"} <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
